import { ALL_BOOKINGS_OPTION, EventReportBookingOptions } from '@contexts/event-report/event-report-context.type';
import { EventReportDataType, EventSalesStatsResponse } from '@domain/event-report';
import { CurrentEvent } from '@hooks/events/use-current-event';
import { EventSalesStatsGroupByResponse } from '@hooks/events/use-get-event-sales-stats-groupby';
import { TFunction } from 'i18next';

import { currencyValueFormatter } from '../analytics-utils';

import { EVENT_REPORT_CHECKIN_TYPE } from './config';

const createToolTipTranslations = (isItaly: boolean, t: TFunction<'translation', undefined>) =>
  isItaly
    ? {
        total: t('eventReport:totalRevenueTooltipContentTotalItaly'),
        online: t('eventReport:totalRevenueTooltipContentOnlineItaly'),
        offline: t('eventReport:totalRevenueTooltipContentOfflineItaly'),
      }
    : {
        total: t('eventReport:totalRevenueTooltipContentTotal'),
        online: t('eventReport:totalRevenueTooltipContentOnline'),
        offline: t('eventReport:totalRevenueTooltipContentOffline'),
      };

export const formatDataHighlightSection = ({
  data,
  dataGroupBy,
  isLiveEvent,
  language,
  event,
  t,
  isTodayActive,
  isRefundEnabled,
  bookingFilterValue = ALL_BOOKINGS_OPTION,
}: {
  data: EventSalesStatsResponse;
  dataGroupBy: EventSalesStatsGroupByResponse;
  isLiveEvent: boolean;
  t: TFunction<'translation', undefined>;
  event: CurrentEvent | null;
  language: string;
  isTodayActive: boolean;
  isRefundEnabled: boolean;
  bookingFilterValue?: EventReportBookingOptions;
}) => {
  const today =
    Object.keys(dataGroupBy).length > 0 ? Object.keys(dataGroupBy)[Object.keys(dataGroupBy).length - 1] : undefined;
  const todayBookings = today
    ? Object.keys(dataGroupBy[today].offers).reduce((acc, type) => {
        if (type === EVENT_REPORT_CHECKIN_TYPE) return acc;

        const offers = dataGroupBy[today].offers[type as EventReportDataType];
        return acc + offers.soldItems[bookingFilterValue];
      }, 0)
    : 0;
  const revenueValue = data.event?.revenue[bookingFilterValue];
  const refundValue = data.event?.refundStats.totalAmount ?? 0;
  const eventSoldItemsValue = data.event?.soldItems[bookingFilterValue];
  const addonSoldItemsValue = data.event?.addOns?.soldItems[bookingFilterValue];
  const tooltipTitle =
    bookingFilterValue === ALL_BOOKINGS_OPTION
      ? t('analytics:totalRevenue')
      : `${t('analytics:totalRevenue')} (${t(`eventReport:${bookingFilterValue}Label`)})`;
  const tooltipTranslations = createToolTipTranslations(event?.club.city.country.isoCode === 'IT', t);

  return [
    {
      title: t('analytics:totalRevenue'),
      value: revenueValue ?? 0,
      ...(refundValue > 0 &&
        isRefundEnabled && {
          secondaryText: t('eventReport:totalRevenueRefundLabel', {
            total: currencyValueFormatter(
              data.event?.refundStats.totalAmount ?? 0,
              language,
              data.event.refundStats.currency
            ),
          }),
        }),
      isCurrency: true,
      tooltipContent: {
        title: tooltipTitle,
        content: tooltipTranslations[bookingFilterValue],
      },
    },
    ...(isLiveEvent
      ? []
      : [
          {
            title: t('eventReport:attendeesLabel'),
            value: data.event?.attendeesStats.attendees[bookingFilterValue] ?? 0,
          },
          {
            title: t('eventReport:attendeesRateTitle'),
            value: data.event?.attendeesStats.attendanceRate[bookingFilterValue] ?? 0,
            secondaryText: t('eventReport:attendeesRateTotalLabel', {
              total: data.event?.soldItems[bookingFilterValue] ?? 0,
            }),
            isPercentage: true,
          },
        ]),
    {
      title: t('customers:tableHeader_totalTickets'),
      value: eventSoldItemsValue ?? 0,
      ...(isTodayActive && { secondaryText: t('eventReport:todayBookingsLabel', { total: todayBookings }) }),
    },
    ...(isLiveEvent
      ? [
          {
            title: t('addons:addOnSectionTitle'),
            value: addonSoldItemsValue ?? 0,
          },
        ]
      : []),
    {
      title: t('eventReport:eventViews'),
      value: data.event?.views ?? 0,
    },
    {
      title: t('eventReport:viewsToBookings'),
      value: data.event.views && eventSoldItemsValue ? (eventSoldItemsValue / data.event.views) * 100 : 0,
      isPercentage: true,
    },
  ];
};
